import { useEffect, useState } from "react";
import { getCountFromServer, query, where, orderBy, collection, Timestamp, QueryConstraint } from "firebase/firestore";
import { firebaseHelper, useActiveBalance, useCreditWorkspace } from "ui";
import { projectsDB } from "../libs/firebase";
import { useLoginState } from "../studioxr/auth/LoginState";

// null while loading or when a count failed.
export interface AccountSummary {
  boards: number | null;
  concepts: number | null;
  credits: number | null;
}

const settled = (r: PromiseSettledResult<number>): number | null => (r.status === "fulfilled" ? r.value : null);

/** Boards, credits and concepts since the period began, for the pause offer (#3957). */
export function useAccountSummary(uid: string | undefined, periodStart: number | null | undefined): AccountSummary {
  const permissions = useLoginState((state) => state.permissions);
  const creditWorkspace = useCreditWorkspace(uid ?? null, permissions);
  const { balance } = useActiveBalance(creditWorkspace);
  const [counts, setCounts] = useState<Pick<AccountSummary, "boards" | "concepts">>({ boards: null, concepts: null });

  useEffect(() => {
    let active = true;
    setCounts({ boards: null, concepts: null });
    if (!uid) return;
    const countBoards = (...clauses: QueryConstraint[]) =>
      getCountFromServer(query(projectsDB, where("user", "==", uid), ...clauses)).then((s) => s.data().count);
    // Deleting a board archives it; the Boards page leaves those out.
    const boards = Promise.all([countBoards(), countBoards(where("archive", "==", true))]).then(([all, archived]) => all - archived);
    const concepts =
      typeof periodStart === "number"
        ? getCountFromServer(
            query(
              collection(firebaseHelper.db, "concepts"),
              where("owner_id", "==", uid),
              where("created_at", ">=", Timestamp.fromMillis(periodStart * 1000)),
              orderBy("created_at", "desc"), // uses the existing owner_id + created_at DESC index
            ),
          ).then((s) => s.data().count)
        : Promise.reject(new Error("no period"));
    Promise.allSettled([boards, concepts]).then(([b, c]) => {
      if (active) setCounts({ boards: settled(b), concepts: settled(c) });
    });
    return () => {
      active = false;
    };
  }, [uid, periodStart]);

  return { ...counts, credits: balance ? balance.totalBalance : null };
}
